import { useEffect } from 'react'
import { ApiCustomer } from '@vend/types'
import { PaymentRequestQueryResponse } from '../../../../../functions/invoicing/fetch-invoicing-payment-requests'
import { bulkCancelPaymentRequests } from '../../../../../functions/invoicing/bulk-cancel-payment-requests'
import { PaymentRequestStatus } from '../../../../../functions/invoicing/payment-request-types'
import { useAppDispatch } from '../../../../../state/hooks'
import { setPaymentRequestsAutoCancelled } from '../../../../../state/slices/currentSale/currentSaleSlice'

interface UseAutoCancelPaymentRequestsParams {
  paymentRequests: PaymentRequestQueryResponse[]
  outstandingBalance: number
  saleLocalId: string | null
  customer: ApiCustomer | null
  refetchPaymentRequests: () => Promise<void>
}

/**
 * Custom hook to auto-cancel all pending payment requests
 * when the sum of all pending payment requests exceeds the current sale outstanding balance
 * in order to avoid overpayment scenarios
 *
 * @param paymentRequests - Array of payment requests for the sale
 * @param outstandingBalance - Current outstanding balance of the sale
 * @param saleLocalId - The local ID of the sale
 * @param customer - The customer associated with the sale
 * @param refetchPaymentRequests - Function to refetch payment requests after cancellation
 */
export const useAutoCancelPaymentRequests = ({
  paymentRequests,
  outstandingBalance,
  saleLocalId,
  customer,
  refetchPaymentRequests,
}: UseAutoCancelPaymentRequestsParams) => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    // Only run if all required data is present
    if (paymentRequests.length === 0 || !saleLocalId || !customer?.id) {
      return
    }

    // Filter pending (sent) payment requests
    const pendingRequests = paymentRequests.filter(
      request => request.status === PaymentRequestStatus.SENT
    )

    if (pendingRequests.length === 0) {
      return
    }

    // and calculate their sum
    // Sum of pending payment requests in dollars (requestedAmount is in cents)
    const totalPendingAmount = pendingRequests.reduce(
      (sum, request) => sum + request.requestedAmount / 100,
      0
    )

    // If sum of pending payment requests exceeds current sale outstanding balance, cancel all
    if (totalPendingAmount > outstandingBalance) {
      dispatch(setPaymentRequestsAutoCancelled(true))

      bulkCancelPaymentRequests(saleLocalId, customer.id).then(result => {
        if (result.success) {
          // Refetch payment requests to update the UI
          refetchPaymentRequests()
        }
      })
    }
  }, [
    outstandingBalance,
    paymentRequests,
    saleLocalId,
    customer,
    refetchPaymentRequests,
    dispatch,
  ])
}
